import { isMountayaApiError, isMountayaError } from "@mountaya/client";
import { QueryClient } from "@tanstack/react-query";

/**
 * CACHE_PRESETS is the three cache profiles, expressed as TanStack Query
 * staleTime/gcTime pairs (ms). Server state lives here, never in Zustand.
 */
export const CACHE_PRESETS = {
  // Default user-scoped resources.
  standard: { gcTime: 10 * 60 * 1000, staleTime: 60 * 1000 },
  // Rarely-changing reference data (e.g. activity catalog).
  static: { gcTime: 60 * 60 * 1000, staleTime: 30 * 60 * 1000 },
  // Frequently-changing data revalidated often.
  volatile: { gcTime: 5 * 60 * 1000, staleTime: 0 },
} as const;

/**
 * shouldRetryQuery decides whether TanStack Query attempts a failed query again.
 *
 * It exists to bound a composition. TanStack retries three times by default and
 * `@mountaya/client` retries twice, so one failed call would be twelve requests
 * against a Data API quota of sixty a minute. Turning the SDK's retry off instead
 * would be worse: it is the only layer that sees `Retry-After` and the status, and it
 * already refuses to repeat a network failure for a non-idempotent request.
 *
 * So retry belongs to the layer that actually issued the request, which is the
 * transport:
 *
 *  - A MountayaError has already exhausted the SDK's budget — it honoured
 *    `Retry-After`, it refused to repeat a 4xx, and it stopped. Retrying here
 *    multiplies that rather than adding to it. The SDK's `isMountayaError` answers for the
 *    whole family, including the connection and GraphQL errors, so a call that reached the
 *    platform at all is never attempted twice from this side. It reads the name an error's
 *    class declares, so the bound holds for an error a second copy of the client threw.
 *  - Anything else reaching this point never went through the transport: a bug in a
 *    query-fn, a boundary Zod rejection, a value a browser extension threw. One more
 *    attempt is cheap and occasionally rescues a genuinely transient one.
 *
 * The worst case is TanStack's attempts times the SDK's own. A failure the transport answered
 * is one TanStack attempt, and the SDK sends a request three times at most (once, and twice
 * more for a quota or a failing gateway), so three requests. A failure past the transport is
 * attempted twice, each attempt reaching the transport, so six. {@link retryAnswerRead} widens
 * the first for the two answer reads, and only for a server failure the SDK does not repeat
 * itself (a 500): four attempts of one request each, so four. A failing gateway (a 502, a 503, a
 * 504) stays one attempt the SDK has already repeated, so three, and six stays the worst case.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (isMountayaError(error)) {
    return false;
  }

  return failureCount < 1;
};

/**
 * ANSWER_READ_RETRIES is how many more times the console asks for what a person may use after the
 * server failed to answer.
 */
const ANSWER_READ_RETRIES = 3;

/**
 * TRANSPORT_REPEATED_STATUSES are the server failures `@mountaya/client` sends again itself, as a
 * failing gateway or service that passes. A read that fails with one of them has already been
 * repeated by the time it reaches TanStack Query. A 503 is also how the API answers a console
 * session it could not validate yet (its identity provider failing, or the address past its
 * refused validations), with a `Retry-After` the transport waits out before asking again, within
 * its one-minute budget: nothing about the session was refused, so it is never a sign-out.
 */
const TRANSPORT_REPEATED_STATUSES: readonly number[] = [502, 503, 504];

/**
 * isUnrepeatedServerFailure reports whether a thrown value is a server failure the transport did
 * not repeat itself: a 5xx other than a failing gateway's, such as a 500.
 */
const isUnrepeatedServerFailure = (error: unknown): boolean =>
  isMountayaApiError(error) &&
  error.status >= 500 &&
  !TRANSPORT_REPEATED_STATUSES.includes(error.status);

/**
 * retryAnswerRead decides whether TanStack Query asks again for the memberships, or for the
 * personal space's answer, after a failure.
 *
 * It widens {@link shouldRetryQuery} for a server failure the transport does not repeat itself,
 * and for nothing else. The membership read resolves what each organization's agreement offers,
 * and one organization's entitlement read failing answers the whole list 500 rather than a
 * membership stripped of its products (ADR 0104): the shell degrades to offering nothing
 * meanwhile, and asks again, three more times on the default backoff, rather than leaving a
 * person without their organizations until the next navigation. A 502, a 503 and a 504 are not
 * asked again here, since the transport repeated each already and a second policy would multiply
 * its attempts: {@link answerReadRecoveryInterval} asks again later. A refusal is not asked again
 * either: a 4xx says the same thing twice.
 */
export const retryAnswerRead = (failureCount: number, error: unknown): boolean =>
  isUnrepeatedServerFailure(error)
    ? failureCount < ANSWER_READ_RETRIES
    : shouldRetryQuery(failureCount, error);

/**
 * ANSWER_READ_RECOVERY_INTERVAL_MS is how often the console asks again for what a person may use
 * while the read holds no answer and stays failed for a reason that passes, once its retries are
 * spent.
 */
export const ANSWER_READ_RECOVERY_INTERVAL_MS = 30_000;

/**
 * isPassingFailure reports whether asking again later can mend a failure: a server failure (a
 * 5xx), or a request that never reached the server (a dropped connection). A refusal (a 401, a
 * 403, a 404) and a body the boundary schema rejects say the same thing every time they are
 * asked, so polling them spends requests on an answer that cannot change.
 */
const isPassingFailure = (error: unknown): boolean =>
  isMountayaApiError(error)
    ? error.status >= 500
    : isMountayaError(error) && error.name === "MountayaConnectionError";

/**
 * answerReadRecoveryInterval is the `refetchInterval` of the memberships read and of the personal
 * space's: every {@link ANSWER_READ_RECOVERY_INTERVAL_MS} while the read holds no answer and its
 * last attempt failed for a reason that passes, and never once an answer has landed, nor for a
 * refusal. It takes over where {@link retryAnswerRead} stops, so the console recovers what a
 * person may use without a navigation.
 *
 * A refresh that fails after an answer landed is not polled: the answer is kept and still offers
 * what it offered, and the read is asked again on its own schedule (staleness, focus, a write
 * that invalidates it), as every other read is.
 *
 * @param query - The read's query, whose last state decides.
 * @returns The interval, or false for none.
 */
export const answerReadRecoveryInterval = (query: {
  readonly state: { readonly data: unknown; readonly error: unknown; readonly status: string };
}): number | false =>
  query.state.data === undefined &&
  query.state.status === "error" &&
  isPassingFailure(query.state.error)
    ? ANSWER_READ_RECOVERY_INTERVAL_MS
    : false;

/**
 * createQueryClient builds the app's TanStack Query client with the standard
 * preset as the default. Render-free: the <QueryClientProvider> binding lives
 * in the app shell.
 *
 * Mutations are declared non-retrying rather than left to inherit it. TanStack already
 * defaults them that way, but the bound {@link shouldRetryQuery} documents depends on
 * it, so it is stated where it can be read beside the arithmetic.
 */
export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      mutations: {
        retry: false,
      },
      queries: {
        gcTime: CACHE_PRESETS.standard.gcTime,
        retry: shouldRetryQuery,
        staleTime: CACHE_PRESETS.standard.staleTime,
      },
    },
  });
